import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AlertTriangle, Download, ExternalLink, RefreshCw, Link2, Send } from "lucide-react";
import {
  closeBookingIssue,
  deleteBookingIssue,
  listBookingIssues,
  listSitesLite,
  loadPicklists,
  notifyNcrExternal,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";

const STATUS_STYLE = {
  open:          "bg-rose-100 text-rose-700",
  investigating: "bg-amber-100 text-amber-700",
  closed:        "bg-emerald-100 text-emerald-700",
};

export default function IssuesInbox() {
  const { user } = useAuth();
  const { viewAsClientId } = useViewAsClient();
  const [rows, setRows]     = useState([]);
  const [sites, setSites]   = useState([]);
  const [types, setTypes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick]     = useState(0);

  // Filters
  const [siteId, setSiteId]     = useState("");
  const [issueType, setIssueType] = useState("");
  const [status, setStatus]     = useState("");
  const [search, setSearch]     = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [r, { data: s }, p] = await Promise.all([
        listBookingIssues({
          siteId: siteId || null,
          status: status || null,
          clientId: viewAsClientId || null,
        }),
        listSitesLite(),
        loadPicklists(),
      ]);
      if (cancelled) return;
      if (r.error) toast.error(r.error.message);
      setRows(r.data || []);
      setSites(s || []);
      setTypes(p?.issue_type || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [siteId, status, viewAsClientId, tick]);

  const filtered = useMemo(() => {
    let arr = rows;
    if (issueType) arr = arr.filter((r) => r.issue_type === issueType);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((r) =>
        (r.title || "").toLowerCase().includes(q) ||
        (r.body  || "").toLowerCase().includes(q) ||
        (r.booking?.reference_number || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [rows, issueType, search]);

  const counts = useMemo(() => {
    return {
      total: filtered.length,
      open: filtered.filter((r) => r.status === "open").length,
      investigating: filtered.filter((r) => r.status === "investigating").length,
      closed: filtered.filter((r) => r.status === "closed").length,
    };
  }, [filtered]);

  const refresh = () => setTick((k) => k + 1);

  const doClose = async (r) => {
    const notes = window.prompt("Resolution notes (optional):", "");
    if (notes === null) return;
    const { error } = await closeBookingIssue(r.id, notes, user?.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Closed.");
    refresh();
  };

  const doDelete = async (r) => {
    if (!window.confirm("Delete this issue permanently?")) return;
    const { error } = await deleteBookingIssue(r.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Deleted.");
    refresh();
  };

  const exportCSV = () => {
    const header = [
      "issue_id","raised_at","status","issue_type","title","body",
      "booking_ref","site","sample_position","sample_temp_c","resolution_notes",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of filtered) {
      lines.push([
        r.id,
        r.raised_at,
        r.status,
        r.issue_type,
        escape(r.title),
        escape(r.body),
        r.booking?.reference_number || "",
        r.site?.name || "",
        r.sample_position ?? "",
        r.sample_temp_c ?? "",
        escape(r.resolution_notes),
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ncr-inbox-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} rows.`);
  };

  return (
    <div data-testid="issues-inbox-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-500" /> NCR / Issues inbox
          </h1>
          <p className="text-sm text-slate-500">
            Cross-booking QA register. Filter, drill in, close, or export.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={refresh} data-testid="issues-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={exportCSV} disabled={!filtered.length} data-testid="issues-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Status chips */}
      <div className="flex flex-wrap gap-2">
        <Chip label="Open" n={counts.open} active={status === "open"}
          onClick={() => setStatus(status === "open" ? "" : "open")} testid="chip-open" />
        <Chip label="Investigating" n={counts.investigating} active={status === "investigating"}
          onClick={() => setStatus(status === "investigating" ? "" : "investigating")} testid="chip-investigating" />
        <Chip label="Closed" n={counts.closed} active={status === "closed"}
          onClick={() => setStatus(status === "closed" ? "" : "closed")} testid="chip-closed" />
        <Chip label="All" n={counts.total} active={status === ""}
          onClick={() => setStatus("")} testid="chip-all" />
      </div>

      <Card>
        <CardHeader className="py-3 space-y-0">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <Input placeholder="Search…" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="issues-search-input" />
            <Select value={siteId || "_all_"} onValueChange={(v) => setSiteId(v === "_all_" ? "" : v)}>
              <SelectTrigger data-testid="issues-site-filter"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={issueType || "_all_"} onValueChange={(v) => setIssueType(v === "_all_" ? "" : v)}>
              <SelectTrigger data-testid="issues-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All types</SelectItem>
                {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label || t.value}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="px-3 py-6 text-sm text-slate-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-12 text-center text-sm text-slate-400" data-testid="issues-empty">
              No issues match these filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Raised</TableHead>
                  <TableHead className="text-right">&nbsp;</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`inbox-issue-${r.id}`}>
                    <TableCell>
                      <Badge className={`text-[10px] font-mono ${STATUS_STYLE[r.status] || ""}`}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{r.issue_type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{r.title || "(no title)"}</div>
                      {r.body && (
                        <div className="text-xs text-slate-500 max-w-md truncate">{r.body}</div>
                      )}
                      {(r.sample_position || r.sample_temp_c != null) && (
                        <div className="text-[10px] text-slate-500 font-mono">
                          #{r.sample_position ?? "?"} · {r.sample_temp_c ?? "?"} °C
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.booking?.reference_number ? (
                        <Link
                          to={`/dock/checklist/${r.booking_id}`}
                          target="_blank"
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline font-mono"
                        >
                          {r.booking.reference_number}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{r.site?.name || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {new Date(r.raised_at || r.created_at).toLocaleString("en-AU", { hourCycle: "h23" })}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.issue_type === "ncr" && r.public_token && (
                        <Button
                          size="sm" variant="ghost"
                          data-testid={`inbox-copy-link-${r.id}`}
                          title="Copy public NCR link"
                          onClick={() => {
                            const url = `${window.location.origin}/p/ncr/${r.public_token}`;
                            navigator.clipboard.writeText(url);
                            toast.success("Public link copied to clipboard");
                          }}
                        >
                          <Link2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {r.issue_type === "ncr" && r.public_token && r.external_email && (
                        <Button
                          size="sm" variant="ghost"
                          data-testid={`inbox-notify-${r.id}`}
                          title={r.notified_at ? `Re-send to ${r.external_email}` : `Notify ${r.external_email}`}
                          onClick={async () => {
                            const { error } = await notifyNcrExternal(
                              r,
                              r.booking || {},
                              r.site || {},
                            );
                            if (error) toast.error(error.message);
                            else { toast.success(`Notification queued to ${r.external_email}`); refresh(); }
                          }}
                        >
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {r.status !== "closed" && (
                        <Button size="sm" variant="outline"
                          onClick={() => doClose(r)} data-testid={`inbox-close-${r.id}`}>
                          Close
                        </Button>
                      )}
                      <Button size="sm" variant="ghost"
                        onClick={() => doDelete(r)} data-testid={`inbox-delete-${r.id}`}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Chip({ label, n, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
      }`}
    >
      {label} <span className="tabular-nums ml-1 opacity-70">{n}</span>
    </button>
  );
}
